import {useIntl} from 'react-intl';
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {FileUpload, FileUploadHandlerEvent} from "primereact/fileupload";
import {ReactiveTable} from "react-ive-tables";
import useSWR, {mutate} from "swr";
import {DocumentPreview} from "./DocumentPreview";
import {useToast} from "../hooks/useToast";
import {File} from "../types/types";
import {Button} from "primereact/button";
import {saveAs} from "file-saver";
import {useDialog} from "../hooks/useDialog";
import moment from 'moment'
import {PenaltyInvoiceService} from "../services/penalty-invoice-service";
import DeletePenaltyInvoiceFile from "../../components/invoices/DeletePenaltyInvoiceFile";

interface Props {
    selectedRecordId?: number;
    allowEdit: boolean;
    dataKey: string;
    downloadHandler: (fileId: number) => Promise<any>;
    showDownloadTime? : boolean;
    uploadHandler?: (file: any, recordId: number) => Promise<any>;
    deleteHandler?: (fileId: number) => Promise<boolean>;
    parentMutationKey?: string;
}

export const Files: React.FC<Props> = props => {
    const {formatMessage: f} = useIntl();
    const didMountRef = useRef(false);
    const fileUploadRef = useRef<any>(null);
    const toastHelpers = useToast();
    const {showConfirmDialog} = useDialog();
    const penaltyInvoicesService = new PenaltyInvoiceService(f, toastHelpers);
    const [showAnnulPenaltyInvoiceDialog, setShowAnnulPenaltyInvoiceDialog] = useState(false);
    const [selectedFileForDelete, setSelectedFileForDelete] = useState<File>();

    const {data: clientFiles} = useSWR<File[], Error>(props.selectedRecordId ? props.dataKey + props.selectedRecordId : null);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
        }
    }, []);

    
    const fileUploadHandler = (event: FileUploadHandlerEvent) => {
        
        if(props.selectedRecordId === undefined || props.selectedRecordId === null || !props.allowEdit || !props.uploadHandler) return;
        props.uploadHandler(event.files[0], props.selectedRecordId!).then(resp => {
            if(resp) {
                fileUploadRef.current.clear();
                toastHelpers.showToast('success', f({ id: "operationSuccessful" }), f({ id: "fileUploadSuccessful" }), false);
                mutate(props.dataKey + props.selectedRecordId);
                if(props.parentMutationKey) mutate(props.parentMutationKey);
            }
        })
    }

    const handleDownloadFile = (file: File) => {
        if(file.invoiceNumber && file.invoiceNumber !== "") {
            penaltyInvoicesService.downloadPenaltyInvoice(file.id)
                .then(blob => {
                    saveAs(blob, file.fileName);
                    mutate(props.dataKey + props.selectedRecordId)
                })
                
            }else {
                props.downloadHandler(file.id).then(blob => {
                    saveAs(blob, file.fileName);
                    mutate(props.dataKey + props.selectedRecordId);
                });
            }
            
    }


    const handleDeleteFile = (file: File) => {
        if (file.id === undefined || !props.allowEdit || !props.deleteHandler) return;
        if(file.invoiceNumber && file.invoiceNumber !== "") {
            setSelectedFileForDelete(file)
            setShowAnnulPenaltyInvoiceDialog(true);
        }else {
            showConfirmDialog({body: f({id: 'confirmDeleteFile'}, {fileName: file.fileName})}).then((confirmed) => {
                if (!confirmed) return;
                props.deleteHandler!(file.id!).then(res => {
                    if (res) {
                        mutate(props.dataKey + props.selectedRecordId);
                        if(props.parentMutationKey) mutate(props.parentMutationKey);
                    }
                })
            })
        }
    }
    const getColumnTemplateForFiles = () => {
        return {
            operations: (rowData: File) => 
                <div className="flex flex-row  justify-content-center">
                    {rowData.fileName.endsWith(".pdf") && <DocumentPreview  data={rowData} downloadHandler={props.downloadHandler}/>}
                
                    <Button icon={'pi pi-download'} tooltip={f({id: 'download'})}
                            tooltipOptions={{position: 'top'}} onClick={() => handleDownloadFile(rowData)} className="mx-2"/>
                    {props.allowEdit ?
                    <Button icon={'pi pi-trash'} tooltip={f({id: 'delete'})} className={'p-button-danger'}
                            tooltipOptions={{position: 'top'}} onClick={() => handleDeleteFile(rowData)}/>
                    : null}
                </div>,
            
            dateCreated: (rowData: File) => moment(rowData.dateCreated).format('DD-MM-YYYY HH:mm:ss'),
            downloadTime: (rowData: File) => rowData.downloadTime ? moment(rowData.downloadTime).tz("Europe/Sofia").format('DD-MM-YYYY HH:mm:ss') : ""
        }
    }

    const closeAnnulDialog = () => {
        setShowAnnulPenaltyInvoiceDialog(false);
        mutate(props.dataKey + props.selectedRecordId)
        if(props.parentMutationKey) mutate(props.parentMutationKey);
    }

    return <>
        {props.allowEdit ?
            <div className={"col-11 md:col-6"}>
                <FileUpload ref={fileUploadRef} style={{width: "100%"}} chooseLabel={f({id: "chooseFile"})} mode="basic"
                            customUpload={true} uploadHandler={fileUploadHandler} name="demo[]" accept="*"
                            maxFileSize={1000000000000}/>
            </div>
        : null}

        {selectedFileForDelete &&
            <DeletePenaltyInvoiceFile showDialog={showAnnulPenaltyInvoiceDialog} closeCb={closeAnnulDialog} selectedPenaltyInvoiceId={selectedFileForDelete?.id} invoiceNumber={selectedFileForDelete.invoiceNumber}/>
        }

        <ReactiveTable data={clientFiles || []}
                         showHeader={false}
                         ignoreFilters={['operations', 'id', 'fileName', 'filePath', 'downloadTime', 'dateCreated']}
                         specialLabels={{dateCreated: "uploadTime"}}
                         columnOrder={props.showDownloadTime ? ['fileName', 'dateCreated' ,'downloadTime' ,'operations'] : ['fileName', 'dateCreated' ,'operations']}
                         columnTemplate={getColumnTemplateForFiles()}
        />
    </>
};
